export const COCKPIT_ADMIN_PERMISSION = 'Cockpit Admin' as const;
export const COCKPIT_VIEWER_PERMISSION = 'Cockpit Viewer' as const;
export const COCKPIT_CUSTOMER_LICENSE_PERMISSION =
  'Cockpit Customer License' as const;
export const COCKPIT_POWER_USER_PERMISSION = 'Cockpit Power User' as const;
export const COCKPIT_JIT_APPROVER_PERMISSION = 'Cockpit JIT Approver' as const;
export const COCKPIT_APP_DUPLICATOR_PERMISSION =
  'Cockpit App Duplicator' as const;
export const PT_LEADERBOARD_READER_PERMISSION =
  'PT Leaderboard Reader' as const;
export const K8S_OPERATORS_READ_PERMISSION = 'K8S Operators Read' as const;
export const K8S_OPERATORS_WRITE_PERMISSION = 'K8S Operators Write' as const;
export const EVALUATION_READ_PERMISSION = 'Evaluation Read' as const;
export const EVALUATION_EXECUTE_PERMISSION = 'Evaluation Execute' as const;

export const COCKPIT_ROLE_NAMES = [
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_VIEWER_PERMISSION,
  COCKPIT_CUSTOMER_LICENSE_PERMISSION,
  COCKPIT_POWER_USER_PERMISSION,
  COCKPIT_JIT_APPROVER_PERMISSION,
  COCKPIT_APP_DUPLICATOR_PERMISSION,
  PT_LEADERBOARD_READER_PERMISSION,
  K8S_OPERATORS_READ_PERMISSION,
  K8S_OPERATORS_WRITE_PERMISSION,
  EVALUATION_READ_PERMISSION,
  EVALUATION_EXECUTE_PERMISSION,
] as const;

export type CockpitRoleName = (typeof COCKPIT_ROLE_NAMES)[number];

export type CockpitPermissionName =
  | typeof COCKPIT_ADMIN_PERMISSION
  | typeof COCKPIT_VIEWER_PERMISSION
  | typeof COCKPIT_CUSTOMER_LICENSE_PERMISSION;

export const COCKPIT_LOCAL_ROLES = [COCKPIT_ADMIN_PERMISSION] as const;

export const COCKPIT_LOCAL_PERMISSIONS = [
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_VIEWER_PERMISSION,
  COCKPIT_CUSTOMER_LICENSE_PERMISSION,
  COCKPIT_POWER_USER_PERMISSION,
  COCKPIT_JIT_APPROVER_PERMISSION,
  COCKPIT_APP_DUPLICATOR_PERMISSION,
  PT_LEADERBOARD_READER_PERMISSION,
  K8S_OPERATORS_READ_PERMISSION,
  K8S_OPERATORS_WRITE_PERMISSION,
  EVALUATION_READ_PERMISSION,
  EVALUATION_EXECUTE_PERMISSION,
] as const;

export const COCKPIT_APP_DUPLICATOR_PERMISSIONS = [
  COCKPIT_APP_DUPLICATOR_PERMISSION,
] as const;

export const COCKPIT_POV_QUERY_WRITE_PERMISSIONS = [
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_POWER_USER_PERMISSION,
] as const;

export const COCKPIT_SQL_WORKSHEET_PERMISSIONS = [
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_POWER_USER_PERMISSION,
] as const;

const COCKPIT_ROLE_IMPLIES: Readonly<Record<string, readonly string[]>> = {
  [COCKPIT_ADMIN_PERMISSION]: [
    EVALUATION_READ_PERMISSION,
    EVALUATION_EXECUTE_PERMISSION,
  ],
};

export function isKnownCockpitRole(value: string): value is CockpitRoleName {
  return (COCKPIT_ROLE_NAMES as readonly string[]).includes(value);
}

const COCKPIT_GROUP_ROLE_MAPPING_SEPARATOR = '|';

function parseMappedRolesFromEntry({ rolesPart }: { rolesPart: string }) {
  return rolesPart
    .split(COCKPIT_GROUP_ROLE_MAPPING_SEPARATOR)
    .map((role) => role.trim())
    .filter((role) => role.length > 0);
}

export function parseCockpitGroupRoleMapping(
  mapping: string | undefined,
): Record<string, readonly string[]> {
  if (!mapping?.trim()) {
    return {};
  }

  const result = new Map<string, Set<string>>();

  for (const entry of mapping.split(',')) {
    const trimmedEntry = entry.trim();

    if (!trimmedEntry) {
      continue;
    }

    const separatorIndex = trimmedEntry.indexOf('=');

    if (separatorIndex <= 0) {
      continue;
    }

    const group = trimmedEntry.slice(0, separatorIndex).trim();
    const rolesPart = trimmedEntry.slice(separatorIndex + 1).trim();
    const mappedRoles = parseMappedRolesFromEntry({ rolesPart });

    if (group.length === 0 || mappedRoles.length === 0) {
      continue;
    }

    const existingRoles = result.get(group) ?? new Set<string>();

    for (const role of mappedRoles) {
      existingRoles.add(role);
    }

    result.set(group, existingRoles);
  }

  return Object.fromEntries(
    [...result.entries()].map(([group, roles]) => [group, [...roles]]),
  );
}

export function resolveCockpitOpenIdRoles({
  extractedClaimValues,
  groupRoleMapping,
  defaultRole,
}: {
  extractedClaimValues: readonly string[];
  groupRoleMapping: Record<string, readonly string[]>;
  defaultRole: string;
}): string[] {
  const mappedRoles = new Set<string>();

  for (const value of extractedClaimValues) {
    const mappedRolesForGroup = groupRoleMapping[value];

    if (mappedRolesForGroup && mappedRolesForGroup.length > 0) {
      for (const role of mappedRolesForGroup) {
        mappedRoles.add(role);
      }

      continue;
    }

    if (isKnownCockpitRole(value)) {
      mappedRoles.add(value);
    }
  }

  if (mappedRoles.size > 0) {
    return Array.from(mappedRoles);
  }

  return [defaultRole];
}

export function resolveCockpitPermissionsFromRoles(
  roles: readonly string[],
): string[] {
  const result = new Set<string>();

  for (const role of roles) {
    result.add(role);
    const implied = COCKPIT_ROLE_IMPLIES[role];

    if (implied) {
      for (const permission of implied) {
        result.add(permission);
      }
    }
  }

  return Array.from(result);
}

export const COCKPIT_LICENSE_WRITE_PERMISSIONS = [
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_CUSTOMER_LICENSE_PERMISSION,
] as const satisfies ReadonlyArray<CockpitPermissionName>;

export const COCKPIT_ENDPOINT_OPS_PERMISSIONS = [
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_CUSTOMER_LICENSE_PERMISSION,
] as const satisfies ReadonlyArray<CockpitPermissionName>;

export const COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS = [
  COCKPIT_ADMIN_PERMISSION,
  COCKPIT_CUSTOMER_LICENSE_PERMISSION,
] as const satisfies ReadonlyArray<CockpitPermissionName>;

export function hasAnyCockpitPermission({
  permissions,
  requiredPermissions,
}: {
  permissions: readonly string[];
  requiredPermissions: readonly string[];
}): boolean {
  return requiredPermissions.some((permission) =>
    permissions.includes(permission),
  );
}

export function canEditCockpitLicense({
  permissions,
}: {
  permissions: readonly string[];
}): boolean {
  return hasAnyCockpitPermission({
    permissions,
    requiredPermissions: COCKPIT_LICENSE_WRITE_PERMISSIONS,
  });
}

export function canRunCockpitEndpointOps({
  permissions,
}: {
  permissions: readonly string[];
}): boolean {
  return hasAnyCockpitPermission({
    permissions,
    requiredPermissions: COCKPIT_ENDPOINT_OPS_PERMISSIONS,
  });
}

export function canEditCockpitFeatureFlags({
  permissions,
}: {
  permissions: readonly string[];
}): boolean {
  return hasAnyCockpitPermission({
    permissions,
    requiredPermissions: COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS,
  });
}

export function isCockpitAdmin({
  permissions,
}: {
  permissions: readonly string[];
}): boolean {
  return permissions.includes(COCKPIT_ADMIN_PERMISSION);
}

export function canDuplicateCockpitApplication({
  permissions,
}: {
  permissions: readonly string[];
}): boolean {
  return hasAnyCockpitPermission({
    permissions,
    requiredPermissions: COCKPIT_APP_DUPLICATOR_PERMISSIONS,
  });
}
